import moment from 'moment';

/*
 * Regras puras dos cadastros de atendimento do administrativo (Coordenadores
 * da Central de Bolsas e Responsáveis): formatação de datas, iniciais para o
 * Avatar, montagem dos corpos enviados ao back e validação da vigência.
 */

export const TAMANHO_MAXIMO_CARGO = 200;
export const TAMANHO_MAXIMO_MOTIVO = 200;
export const TAMANHO_PAGINA_FUNCIONARIOS = 5;

export const FORMATO_DATA_ISO = 'YYYY-MM-DD';
export const FORMATO_DATA_TELA = 'DD/MM/YYYY';

export const MENSAGEM_FIM_ANTERIOR_AO_INICIO = 'A data de fim de vigência deve ser igual ou posterior à de início.';
export const MENSAGEM_RESPONSAVEL_ADICIONADO = 'Responsável adicionado com sucesso!';
export const MENSAGEM_RESPONSAVEL_EXCLUIDO = 'Responsável excluído com sucesso!';
export const MENSAGEM_ATENDENTE_ADICIONADO = 'Atendente adicionado com sucesso!';
export const MENSAGEM_ATENDENTE_EXCLUIDO = 'Atendente excluído com sucesso!';
export const CONFIRMACAO_EXCLUIR_ATENDENTE = 'Tem certeza que deseja remover o atendente?';

const REGEX_DATA_ISO = /^(\d{4})-(\d{2})-(\d{2})/;

/*
 * Converte o valor em moment: aceita moment, Date ou texto ISO (`yyyy-MM-dd`,
 * com ou sem hora). Devolve nulo quando vazio ou inválido.
 */
export const paraMoment = valor => {
    if (valor === null || valor === undefined || valor === '') {
        return null;
    }

    if (moment.isMoment(valor)) {
        return valor.isValid() ? valor : null;
    }

    if (valor instanceof Date) {
        return moment(valor).isValid() ? moment(valor) : null;
    }

    const partes = REGEX_DATA_ISO.exec(String(valor));
    if (!partes) {
        return null;
    }

    const data = moment(`${partes[1]}-${partes[2]}-${partes[3]}`, FORMATO_DATA_ISO, true);

    return data.isValid() ? data : null;
};

/* ISO (`yyyy-MM-dd`) -> `dd/MM/yyyy`; vazio quando ausente ou inválido. */
export const formatarDataIso = iso => {
    const data = paraMoment(iso);

    return data ? data.format(FORMATO_DATA_TELA) : '';
};

/* moment/Date/ISO -> `yyyy-MM-dd`; nulo quando vazio ou inválido. */
export const dataParaIso = valor => {
    const data = paraMoment(valor);

    return data ? data.format(FORMATO_DATA_ISO) : null;
};

/*
 * Iniciais para o Avatar sem foto: primeira letra do primeiro e do último nome
 * (uma só quando o nome tem uma palavra). Vazio quando não há nome.
 */
export const iniciaisNome = nome => {
    const palavras = String(nome ?? '').trim().split(/\s+/).filter(Boolean);

    if (palavras.length === 0) {
        return '';
    }

    const primeira = Array.from(palavras[0])[0];
    if (palavras.length === 1) {
        return primeira.toLocaleUpperCase('pt-BR');
    }

    const ultima = Array.from(palavras[palavras.length - 1])[0];

    return (primeira + ultima).toLocaleUpperCase('pt-BR');
};

/* Texto sem espaços nas pontas; vazio vira nulo. */
export const textoOuNulo = valor => {
    const texto = String(valor ?? '').trim();

    return texto === '' ? null : texto;
};

/*
 * Vigência válida: sem fim (ou início) ainda não há o que comparar; com os dois,
 * o fim tem de ser igual ou posterior ao início (comparação por dia).
 */
export const fimNaoAnteriorAoInicio = (inicio, fim) => {
    const dataInicio = paraMoment(inicio);
    const dataFim = paraMoment(fim);

    if (!dataInicio || !dataFim) {
        return true;
    }

    return !dataFim.isBefore(dataInicio, 'day');
};

/* Valores do formulário do diálogo de coordenador (datas como moment). */
export const VALORES_VAZIOS_COORDENADOR = Object.freeze({
    idPessoa: '',
    nomePessoa: '',
    idCampus: '',
    cargo: '',
    dataInicioVigencia: null,
    dataFimVigencia: null,
    motivoFimVigencia: ''
});

/* Registro devolvido pelo back -> valores do formulário. */
export const paraFormularioCoordenador = item => {
    if (!item) {
        return {...VALORES_VAZIOS_COORDENADOR};
    }

    const temValor = valor => valor !== null && valor !== undefined;

    return {
        idPessoa: temValor(item.idPessoa) ? String(item.idPessoa) : '',
        nomePessoa: item.nome || '',
        idCampus: item.campus && temValor(item.campus.id) ? String(item.campus.id) : '',
        cargo: item.cargo || '',
        dataInicioVigencia: paraMoment(item.dataInicioVigencia),
        dataFimVigencia: paraMoment(item.dataFimVigencia),
        motivoFimVigencia: item.motivoFimVigencia || ''
    };
};

/* Corpo de POST/PUT de coordenador: datas ISO, textos com trim e vazios como nulo. */
export const montarPayloadCoordenador = valores => ({
    idPessoa: Number(valores?.idPessoa),
    idCampus: Number(valores?.idCampus),
    cargo: String(valores?.cargo ?? '').trim(),
    dataInicioVigencia: dataParaIso(valores?.dataInicioVigencia),
    dataFimVigencia: dataParaIso(valores?.dataFimVigencia),
    motivoFimVigencia: textoOuNulo(valores?.motivoFimVigencia)
});

/* Corpo de POST de responsável. */
export const montarPayloadResponsavel = idPessoa => ({idPessoa: Number(idPessoa)});

/* Corpo de POST de atendente. */
export const montarPayloadAtendente = idPessoa => ({idPessoa: Number(idPessoa)});

/* Parâmetros da busca paginada de funcionários (filtros já vêm como JSON do DataGrid). */
export const montarParametrosFuncionarios = (filtros, pagina, tamanhoPagina = TAMANHO_PAGINA_FUNCIONARIOS) => ({
    filtros: filtros ? String(filtros) : '{}',
    paginaAtual: pagina || 0,
    tamanhoPagina
});
